import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PiggyBank, RefreshCw, Download, ChevronDown, ChevronRight, Eye, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { RETENTION_PARTS, buildRetentionEntries, buildRetentionLedger, summarizeRetention } from '../payroll/core/retentionLedger';
import { loadRetentionData } from '../payroll/data/reportActions';
import { PAYROLL_PATHS, canViewPayrollPage } from '../payroll/data/permissions';
import { downloadWorkbook } from '../payroll/components/excelIO';
import {
    AccessDenied, Badge, EmptyState, FilterField, IconAction, ListToolbar, PageShell, Pagination, SortableTh, Spinner, StatCards, buttonSecondary,
    formatDay, formatMoney, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, usePagination, useSortedRows,
} from '../payroll/components/ui';

const periodLabel = (entry) => `${String(entry.thang).padStart(2, '0')}/${entry.nam}`;

const SoTienGiuLai = () => {
    const navigate = useNavigate();
    const canView = canViewPayrollPage(PAYROLL_PATHS.retention);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState('ALL');
    const [team, setTeam] = useState('ALL');
    const [search, setSearch] = useState('');
    const [onlyLeft, setOnlyLeft] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setData(await loadRetentionData());
        } catch (error) {
            console.error('Lỗi tải sổ tiền giữ lại:', error);
            toast.error('Không tải được sổ tiền giữ lại');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const entries = useMemo(() => (data ? buildRetentionEntries(data.periods, data.details) : []), [data]);
    const years = useMemo(() => [...new Set(entries.map((entry) => entry.nam))].sort((a, b) => b - a), [entries]);
    const ledger = useMemo(() => buildRetentionLedger(
        entries.filter((entry) => year === 'ALL' || entry.nam === Number(year)),
        data?.employees ?? [],
    ), [entries, year, data]);
    const teams = useMemo(() => [...new Set(ledger.map((row) => row.to))].sort((a, b) => a.localeCompare(b, 'vi', { numeric: true })), [ledger]);
    const filtered = useMemo(() => ledger
        .filter((row) => team === 'ALL' || row.to === team)
        .filter((row) => !onlyLeft || row.left)
        .filter((row) => matchesSearch(`${row.hoTen} ${row.soHd} ${row.to}`, search)), [ledger, team, onlyLeft, search]);
    const totals = useMemo(() => summarizeRetention(filtered), [filtered]);
    const accessors = useMemo(() => ({
        name: (row) => row.hoTen, team: (row) => row.to, periods: (row) => row.periodCount,
        ...Object.fromEntries(RETENTION_PARTS.map(({ key }) => [key, (row) => row[key]])), total: (row) => row.total,
    }), []);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors, { key: 'total', direction: 'desc' });
    const { pageRows, pagination } = usePagination(sorted, 50);

    if (!canView) return <AccessDenied />;

    const toggle = (soHd) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(soHd)) next.delete(soHd); else next.add(soHd);
        return next;
    });
    const scope = year === 'ALL' ? 'tất cả các năm' : `năm ${year}`;
    const handleExport = () => {
        try {
            const partLabels = RETENTION_PARTS.map(({ label }) => label);
            downloadWorkbook(`SoTienGiuLai_${year === 'ALL' ? 'TatCa' : year}.xlsx`, [
                {
                    name: 'Theo người',
                    rows: [
                        ['SỐ HĐ', 'HỌ TÊN', 'TỔ', 'SỐ KỲ', ...partLabels, 'TỔNG ĐANG GIỮ', 'TÌNH TRẠNG'],
                        ...sorted.map((row) => [row.soHd, row.hoTen, row.to, row.periodCount, ...RETENTION_PARTS.map(({ key }) => row[key]), row.total, row.left ? 'Đã nghỉ' : '']),
                        ['', 'TỔNG CỘNG', '', '', ...RETENTION_PARTS.map(({ key }) => totals[key]), totals.total, ''],
                    ],
                },
                {
                    name: 'Chi tiết kỳ',
                    rows: [
                        ['KỲ', 'SỐ HĐ', 'HỌ TÊN', 'TỔ', ...partLabels, 'TỔNG'],
                        ...sorted.flatMap((row) => row.entries.map((entry) => [periodLabel(entry), row.soHd, row.hoTen, entry.to, ...RETENTION_PARTS.map(({ key }) => entry[key]), entry.total])),
                    ],
                },
            ]);
        } catch (error) {
            toast.error(`Không xuất được Excel: ${error.message}`);
        }
    };

    return (
        <PageShell icon={PiggyBank} title="Sổ tiền giữ lại" subtitle="PCTN giữ lại và tiền giữ lại theo khối lượng, cộng dồn từ các kỳ lương đã duyệt"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    <button className={buttonSecondary} onClick={handleExport} disabled={!sorted.length}><Download className="w-4 h-4" />Xuất Excel</button>
                </>
            )}>
            <StatCards cards={[
                { key: 'people', label: 'Người đang bị giữ', value: totals.people, tone: 'indigo', caption: `Tính ${scope}` },
                { key: 'pctn', label: 'PCTN giữ lại', value: `${formatMoney(totals.pctn)} đ`, tone: 'yellow' },
                { key: 'weight', label: 'Giữ lại theo KL', value: `${formatMoney(totals.weight)} đ`, tone: 'green' },
                { key: 'total', label: 'Tổng đang giữ', value: `${formatMoney(totals.total)} đ`, tone: 'purple', caption: totals.leftCount ? `${totals.leftCount} người đã nghỉ còn tiền giữ` : 'Chưa có người đã nghỉ' },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm tên, số HĐ hoặc tổ..."
                filterCount={[team !== 'ALL', onlyLeft].filter(Boolean).length}
                onClearFilters={() => { setTeam('ALL'); setOnlyLeft(false); setSearch(''); }}
                filters={(
                    <>
                        <FilterField label="Tổ">
                            <select className={inputClass} value={team} onChange={(e) => setTeam(e.target.value)}>
                                <option value="ALL">Tất cả tổ</option>
                                {teams.map((item) => <option key={item} value={item}>{item}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Tình trạng">
                            <label className="flex items-center gap-2 text-sm text-gray-700 py-2">
                                <input type="checkbox" checked={onlyLeft} onChange={(e) => setOnlyLeft(e.target.checked)} />
                                Chỉ người đã nghỉ việc
                            </label>
                        </FilterField>
                    </>
                )}
                extra={(
                    <select className={`${inputClass} w-auto`} value={year} onChange={(e) => setYear(e.target.value)} aria-label="Năm">
                        <option value="ALL">Tất cả các năm</option>
                        {years.map((item) => <option key={item} value={item}>Năm {item}</option>)}
                    </select>
                )} />

            {loading && !data ? <Spinner label="Đang tải sổ tiền giữ lại..." /> : !sorted.length ? (
                <EmptyState icon={PiggyBank} title="Chưa có tiền giữ lại">
                    Sổ chỉ ghi nhận từ các kỳ lương đã duyệt có PCTN giữ lại hoặc tiền giữ lại theo khối lượng. Đặt tỷ lệ và đơn giá giữ lại ở trang Thỏa thuận công lương.
                </EmptyState>
            ) : (
                <>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <th className={`${thClass} w-8`} />
                                    <SortableTh label="Nhân sự" sortKey="name" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Tổ" sortKey="team" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Số kỳ" sortKey="periods" sort={sort} onSort={toggleSort} align="right" />
                                    {RETENTION_PARTS.map(({ key, label }) => <SortableTh key={key} label={label} sortKey={key} sort={sort} onSort={toggleSort} align="right" />)}
                                    <SortableTh label="Tổng đang giữ" sortKey="total" sort={sort} onSort={toggleSort} align="right" />
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((row) => {
                                    const open = expanded.has(row.soHd);
                                    return (
                                        <Fragment key={row.soHd}>
                                            <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggle(row.soHd)}>
                                                <td className={`${tdClass} text-gray-400`}>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                                                <td className={tdClass}>
                                                    <div className="font-medium text-gray-900">{row.hoTen}</div>
                                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                                        {row.soHd}
                                                        {row.left && <Badge tone="red" title={row.leftOn ? `Nghỉ từ ${formatDay(row.leftOn)}` : undefined}>Đã nghỉ{row.leftOn ? ` ${formatDay(row.leftOn)}` : ''}</Badge>}
                                                    </div>
                                                </td>
                                                <td className={tdClass}>{row.to}</td>
                                                <td className={`${tdClass} text-right`}>{row.periodCount}<div className="text-xs text-gray-400">đến {row.lastPeriod}</div></td>
                                                {RETENTION_PARTS.map(({ key }) => <td key={key} className={`${tdClass} text-right whitespace-nowrap`}>{formatMoney(row[key])}</td>)}
                                                <td className={`${tdClass} text-right whitespace-nowrap font-semibold text-gray-900`}>{formatMoney(row.total)}</td>
                                            </tr>
                                            {open && row.entries.map((entry) => (
                                                <tr key={entry.id} className="bg-indigo-50/40 text-xs">
                                                    <td />
                                                    <td className="px-3 py-1.5 text-gray-600">Kỳ {periodLabel(entry)}</td>
                                                    <td className="px-3 py-1.5 text-gray-600">{entry.to}</td>
                                                    <td className="px-3 py-1.5 text-right">
                                                        <IconAction icon={Eye} title="Mở bảng lương" tone="blue" onClick={(event) => { event.stopPropagation(); navigate(`/bangcongluong/${entry.maKy}`); }} />
                                                    </td>
                                                    {RETENTION_PARTS.map(({ key }) => <td key={key} className="px-3 py-1.5 text-right whitespace-nowrap">{formatMoney(entry[key])}</td>)}
                                                    <td className="px-3 py-1.5 text-right whitespace-nowrap font-medium">{formatMoney(entry.total)}</td>
                                                </tr>
                                            ))}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 font-semibold text-gray-900 text-sm">
                                    <td className="px-3 py-3 text-center" colSpan={4}>TỔNG CỘNG ({sorted.length} người)</td>
                                    {RETENTION_PARTS.map(({ key }) => <td key={key} className="px-3 py-3 text-right whitespace-nowrap">{formatMoney(totals[key])}</td>)}
                                    <td className="px-3 py-3 text-right whitespace-nowrap">{formatMoney(totals.total)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <Pagination pagination={pagination} unit="người" />
                </>
            )}
            <p className="mt-4 flex items-start gap-2 text-xs text-gray-500">
                <Info className="w-4 h-4 shrink-0" />
                Tiền giữ lại chỉ ghi nhận từ các kỳ đã duyệt trên app (PCTN_GL và KL_GL trên bảng lương); phần trả lại cuối năm hoặc khi nghỉ việc sẽ làm sau.
                Kỳ được mở lại sẽ rời khỏi sổ đến khi duyệt lại.
            </p>
        </PageShell>
    );
};

export default SoTienGiuLai;
